// Troubleshooting.tsx
import React, { useState, useEffect } from 'react';
import { marked } from 'marked';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  IconStar,
  IconLink,
} from 'src/components/Icons'; // adjust if needed
import vibration from '../assets/troubleshooting/vibration.png';

/* ───────────────────────── section keys ───────────────────────── */
type SectionKey =
  | 'emptyScreen'
  | 'instantlyCloses'
  | 'connectionIssues'
  | 'stabilityIssues'
  | 'proVersionIssues'
  | 'accountIssues'
  | 'deckVisibility'
  | 'buttonPress'
  | 'imageLoading'
  | 'featureIssues'
  | 'switchDeckIssues'
  | 'twitchEmbed'
  | 'vibration';

/* ───────────────────────── helpers ───────────────────────── */
const refs: Record<SectionKey, HTMLElement | null> = {} as any;

const copyAnchor = async (anchor: SectionKey) => {
  const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    const area = document.createElement('textarea');
    area.value = url;
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
  }
};

/* ───────────────────────── row component ───────────────────────── */
interface RowProps {
  title: string;
  markdown: string;
  anchor: SectionKey;
  isOpen: boolean;
  isPro?: boolean;
  onToggle: () => void;
}

const Row: React.FC<RowProps> = ({
  title,
  markdown,
  anchor,
  isOpen,
  isPro,
  onToggle,
}) => (
  <div
    ref={(el) => (refs[anchor] = el)}
    className="scroll-mt-24 flex flex-col w-full my-2 p-1 md:mx-3 border-b rounded-lg border-gray-600/50"
  >
    <button
      className={`flex justify-between items-center w-full gap-5 rounded-lg p-3 text-left ${
        isOpen ? 'bg-blue-600/50' : ''
      } text-gray-300 hover:bg-blue-600/50`}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`${anchor}-content`}
    >
      <h2 className="text-lg">
        {title} {isPro && <IconStar />}
      </h2>

      <div className="flex items-center space-x-2">
        <span
          onClick={(e) => {
            e.stopPropagation();
            copyAnchor(anchor);
          }}
          title="Copy link"
          className="text-gray-400 hover:text-gray-200"
        >
          <IconLink className="w-5 h-5" />
        </span>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </div>
    </button>

    {isOpen && (
      <div
        id={`${anchor}-content`}
        className="rounded-lg bg-bgBorderBlue bg-opacity-30 py-6 px-4 text-gray-300"
        dangerouslySetInnerHTML={{ __html: marked(markdown) }}
      />
    )}
  </div>
);

/* ───────────────────────── markdown data ───────────────────────── */
const md: Record<SectionKey, string> = {
    // Markdown contents
        emptyScreen: `
1. **Android System WebView is not installed or is an outdated version on your device** 
    - You can download/update WebView from [Google Play Store](https://play.google.com/store/apps/details?id=com.google.android.webview&hl=en_CA&gl=US). Please ensure your device is using Android System WebView or Chrome WebView.    
    `,
        instantlyCloses: `
1. **Check your antivirus or security software**
    - Some antivirus or security software may have additional features that could block Deck Hopper from running.
</br></br>
2. **Try running Deck Hopper as administrator**
    - Right click on Deck Hopper and select "Run as administrator".
</br></br>
3. **Temporarily disable your antivirus and redownload Deck Hopper**
    - Temporarily disable your antivirus and redownload Deck Hopper, as some files may have been quarantined.
    - If this works, add Deck Hopper to your antivirus' whitelist.
</br></br>
4. **Download Unpacked version instead of Installer**
    - Download the Unpacked Deck Hopper instead of the Installer.
    - Unpacked version is available on [Itch download page](https://christinak.itch.io/deck-hopper)
    `,
        connectionIssues: `
1. **Mismatched versions**
    - Ensure that you are using the most recent version of Deck Hopper.
    - Ensure that you are using the most recent version of SAMMI Core.
</br></br>
2. **Non Local Network**
   - Double check you're on the same local network as the device running SAMMI Core.
</br></br>
3. **Incorrect IP/Port or Password**
   - Verify that the IP address, Port, and Password entered in Deck Hopper match the IP address, Port, and Password in your SAMMI Settings.
    - The IP address is "127.0.0.1" if SAMMI Core is running on the same device as Deck Hopper. 
    - If SAMMI Core is running on a different device, you will need to enter the IP address of that device: 
        - Open Command Prompt on the device running SAMMI Core, and type "ipconfig".
        - Find the IPv4 Address of your ethernet or wifi adapter. Make sure it's **not** the IPv4 Address of your VPN or virtual adapter, such as Hyper-V Virtual Ethernet Adapter. 
    - The default port is 9470.
</br></br>
4. **Firewall Interference**
   - Check that your firewall is not preventing Deck Hopper from connecting. Try adding SAMMI Core to your firewall's whitelist.
</br></br>
5. **Antivirus or Security Software Interference**
   - Some antivirus or security software may have additional features that could block the connection. Try adding SAMMI Core to your antivirus' whitelist, or temporarily disabling your antivirus.
</br></br>
6. **WebView Compatibility**
    - Update or install Chrome or Android System Webview from the Google Play Store. Deck Hopper relies on WebView to function properly, and compatibility issues can prevent connection to SAMMI Core.
    - If your device is running a custom OS or an older version of Android, ensure that you are using Google Chrome WebView.
    - You can verify your current WebView implementation by going to System - Developer Options - WebView Implementation.
</br></br>
7. **Running Multiple Instances**
   - Ensure that only one instance of SAMMI Core is running, and you're not connecting to the wrong instance.
    `,
        stabilityIssues: `
1. **Unstable connection to your local network**
    - Check the stability of your local network connection.
</br></br>
2. **Wrong data being sent to SAMMI Core**
    - Verify that the correct data is being sent to SAMMI Core. If you get disconnected after a specific action, e.g., pressing a button, try to reproduce the action and see if you get disconnected again. File a bug report by navigating to Help tab - Report a bug.
</br></br>
3. **SAMMI Core related issue**
    - Consider if a recent update to SAMMI Core has introduced a bug, and try downgrading your SAMMI Core.
</br></br>
4. **Outdated version of Deck Hopper or SAMMI Core**
    - Check for updates for both Deck Hopper and SAMMI Core.
    `,
        proVersionIssues: `
1. **Check your internet connection**
    - Ensure that you have internet connection, as this is required for login persistence, as well as Basic/Pro version persistence.
</br></br>
2. **Check for multiple instances of Deck Hopper (Desktop only)**
    - Ensure that only one instance of Deck Hopper is running.
    - Note: You can have multiple windows open, but only one instance of Deck Hopper.
</br></br>
3. **Contact support**
    - If the issue persists, [contact support](https://christinak.ca/deckhopper/contact) and include: User ID (from Menu - Account tab - Get User ID), your email, and the purchase receipt.
    `,
        accountIssues: `
For account-related issues, please use the [Contact Us](https://christinak.ca/deckhopper/contact) form.
    `,
        deckVisibility: `
1. **Wrong instance of SAMMI Core**
    - Ensure that you are connecting to the correct instance of SAMMI Core. Verify the IP and Port is correct. You can double check your IP address by going to SAMMI Core - Deck App - Get LAN IP. You can double check your port by going to SAMMI Core - Settings - Deck App settings.
</br></br>
2. **Double check your decks are enabled in SAMMI Core**
    - Ensure that the decks you want to see in Deck Hopper are enabled in SAMMI Core.
</br></br>
3. **Check your internet connection**
    - Ensure that you have a stable internet connection to your local network. Try disconnecting and connecting again.
    `,
        buttonPress: `
1. **Check you're still connected to SAMMI Core**
    - Ensure Deck Hopper is still connected to SAMMI Core by exiting the deck and checking the connection status.    
    `,
        imageLoading: `
1. **Ensure you're using the latest version of SAMMI Core**
    - Check for updates by going to SAMMI Core - Settings - Check for Updates. 
    - For this particular functionality, you must be on SAMMI Core 2023.3.0 or later.
</br></br>
2. **Check the image name does not contain special characters**
    - Special characters in the image name may cause issues. Try renaming the image without special characters, and updating the button/deck image with the new name.
</br></br>
3. **Ensure the image size is not too large**
    - Try reducing the image size. All images should be less than 1MB, depending on the device and network speed.
</br></br>
4. **Try clearing cache**
    - On Android, go to Settings - Apps - Deck Hopper - Storage - Clear storage and Clear Cache.
    - On Desktop, go to AppData - Roaming (or Local) - Deck Hopper - Cache - Delete all files.
    `,
        featureIssues: `
1. **Ensure you're using the latest version of SAMMI Core**
    - Check for updates by going to SAMMI Core - Settings - Check for Updates.
    `,
        switchDeckIssues: `
Switch Deck button function has been removed from SAMMI. You can switch decks by using [Deck App: Switch Deck](https://sammi.solutions/docs/commands/308) command in SAMMI Core instead. All SAMMI Deck App commands are compatible with Deck Hopper. You can also navigate between decks by swiping up/down on the deck on mobile (when Hopper is in edit mode), or by using arrow keys on desktop.
    `,
        twitchEmbed: `
1. **Ensure you're logged in as a Pro user in Deck Hopper.**
    - Go to Menu - Account tab - Login.
</br></br>
2. **Try regenerating Embed buttons**
    - Regenerate the embed button by going to SAMMI Core -> Deck Hopper Advanced -> Create Twitch Embed button
</br></br>
3. **Try clearing cache to remove any corrupted files**
    - On Android, go to Settings - Apps - Deck Hopper - Storage - Clear storage and Clear Cache.
    - On Desktop, go to AppData - Roaming (or Local) - Deck Hopper - Cache - Delete all files.
    `,
        vibration: `
Ensure your Android device has vibration enabled in Settings - Sound - Vibration. Deck Hopper uses both Touch interaction and Notifications, so both must be enabled.  
<br>
![Vibration Settings Screenshot](${vibration})
    `,
};

/* ───────────────────────── main component ───────────────────────── */
const Troubleshooting: React.FC = () => {
  const [open, setOpen] = useState<SectionKey | null>(null);

  const openAndScroll = (key: SectionKey) => {
    setOpen((prev) => (prev === key ? null : key));
    setTimeout(() => refs[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as SectionKey;
    if (hash) openAndScroll(hash);
  }, []);

  return (
  <div className="flex flex-col items-center bg-dark-blue text-gray-300 p-4 w-full mx-auto pb-10">
      <h1 className="text-3xl md:text-4xl text-center text-white mt-4 mb-4">
        Troubleshooting
      </h1>
      <p className="text-lg md:text-xl text-center text-gray-400 mb-6">
        Solutions to common Deck Hopper issues.
      </p>
      <hr className="border-t border-gray-600 w-2/3 mb-8" />

      <div className="flex flex-col w-full max-w-screen-lg">
        <Row
          title="Android shows empty/white screen"
          markdown={md.emptyScreen}
          anchor="emptyScreen"
          isOpen={open === 'emptyScreen'}
          onToggle={() => openAndScroll('emptyScreen')}
        />
        <Row
          title="Desktop version instantly closes"
          markdown={md.instantlyCloses}
          anchor="instantlyCloses"
          isOpen={open === 'instantlyCloses'}
          onToggle={() => openAndScroll('instantlyCloses')}
        />
        <Row
          title="Cannot connect to SAMMI"
          markdown={md.connectionIssues}
          anchor="connectionIssues"
          isOpen={open === 'connectionIssues'}
          onToggle={() => openAndScroll('connectionIssues')}
        />
        <Row
          title="Constant disconnect/reconnect"
          markdown={md.stabilityIssues}
          anchor="stabilityIssues"
          isOpen={open === 'stabilityIssues'}
          onToggle={() => openAndScroll('stabilityIssues')}
        />
        <Row
          title="Pro/Basic not recognised"
          markdown={md.proVersionIssues}
          anchor="proVersionIssues"
          isOpen={open === 'proVersionIssues'}
          onToggle={() => openAndScroll('proVersionIssues')}
        />
        <Row
          title="Account problems"
          markdown={md.accountIssues}
          anchor="accountIssues"
          isOpen={open === 'accountIssues'}
          onToggle={() => openAndScroll('accountIssues')}
        />
        <Row
          title="Deck list wrong / missing decks"
          markdown={md.deckVisibility}
          anchor="deckVisibility"
          isOpen={open === 'deckVisibility'}
          onToggle={() => openAndScroll('deckVisibility')}
        />
        <Row
          title="Button press does nothing"
          markdown={md.buttonPress}
          anchor="buttonPress"
          isOpen={open === 'buttonPress'}
          onToggle={() => openAndScroll('buttonPress')}
        />
        <Row
          title="Images not loading"
          markdown={md.imageLoading}
          anchor="imageLoading"
          isOpen={open === 'imageLoading'}
          onToggle={() => openAndScroll('imageLoading')}
        />
        <Row
          title="Feature not working"
          markdown={md.featureIssues}
          anchor="featureIssues"
          isOpen={open === 'featureIssues'}
          onToggle={() => openAndScroll('featureIssues')}
        />
        <Row
          title="Switch Deck button broken"
          markdown={md.switchDeckIssues}
          anchor="switchDeckIssues"
          isOpen={open === 'switchDeckIssues'}
          onToggle={() => openAndScroll('switchDeckIssues')}
        />
        <Row
          title="Missing Twitch Embed"
          markdown={md.twitchEmbed}
          anchor="twitchEmbed"
          isOpen={open === 'twitchEmbed'}
          onToggle={() => openAndScroll('twitchEmbed')}
        />
        <Row
          title="No vibration / haptics"
          markdown={md.vibration}
          anchor="vibration"
          isOpen={open === 'vibration'}
          onToggle={() => openAndScroll('vibration')}
        />
      </div>

      <p className="mt-8 text-center">
        Still stuck? Join the&nbsp;
        <a
          href="https://discord.christinak.ca/"
          className="text-blue-400 underline"
        >
          support Discord
        </a>
        .
      </p>
    </div>
  );
};

export default Troubleshooting;
